// Derivaciones de presentación de una venta, compartidas por la tabla, el hero del detalle y el
// tab «Venta». Viven aquí porque las tres pintaban la misma celda con su propia copia de la regla, y
// una venta a crédito propio salía «SureKeep» en la tabla y sin chip en el hero.
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import type { PillTone } from '@/shared/patterns/Pills';

/**
 * Etiquetas comerciales de las financieras EXTERNAS. Las claves son los cuatro miembros de
 * `ExternalProviderEnum`: SureKeep NO aparece porque no es un proveedor externo — su crédito propio
 * se persiste como `type: 'CREDIT'` con `externalProvider: null`.
 * Un valor fuera del mapa cae a su código crudo, nunca a una celda vacía.
 */
export const PROVIDER_LABEL: Record<string, string> = {
    PAYJOY: 'PayJoy',
    CREDIYA: 'CrediYa',
    LESPAGO: 'LESPAGO',
    INNOVA: 'Innovación',
};

/** Lo mínimo de la venta para resolver su financiera. */
export type FinancierableSale = Pick<BackofficeSale, 'externalProvider' | 'type'>;

/** Rótulos que dependen del locale y por eso entran desde el componente, no se hardcodean aquí. */
export interface FinancieraLabels {
    surekeep: string;
    contado: string;
}

/**
 * La financiera que el operador ve. NO sale de un solo campo: `externalProvider` solo existe para
 * financieras de terceros, así que un crédito propio (`CREDIT` sin proveedor) tiene que resolverse
 * a SureKeep — que es lo que el vendedor eligió en el paso 04. Cualquier otro tipo sin proveedor va
 * `null`, y quien lo pinta decide si eso es un guion o la ausencia del chip.
 */
export function financieraLabel(sale: FinancierableSale, labels: FinancieraLabels): string | null {
    if (sale.externalProvider)
        return PROVIDER_LABEL[sale.externalProvider] ?? sale.externalProvider;
    if (sale.type === 'CREDIT') return labels.surekeep;
    return sale.type === 'CASH' ? labels.contado : null;
}

/** Abreviatura del chip de financiera en la tabla (`fm.short` del mock v3). */
const PROVIDER_SHORT: Record<string, string> = {
    SUREKEEP: 'SK',
    PAYJOY: 'PJ',
    CREDIYA: 'CY',
    LESPAGO: 'LP',
    INNOVA: 'INV',
    CASH: 'CTD',
};

/** Tono del chip de financiera, como el mock: SureKeep verde, PayJoy azul, CrediYa ámbar. */
const PROVIDER_TONE: Record<string, PillTone> = {
    SUREKEEP: 'success',
    PAYJOY: 'info',
    CREDIYA: 'warning',
};

/** La llave de financiera de la venta: el proveedor externo, SureKeep o contado. */
function providerKey(sale: FinancierableSale): string | null {
    if (sale.externalProvider) return sale.externalProvider;
    if (sale.type === 'CREDIT') return 'SUREKEEP';
    return sale.type === 'CASH' ? 'CASH' : null;
}

/** Abreviatura y tono del chip. `null` cuando la venta no tiene financiera resoluble. */
export function providerChip(sale: FinancierableSale): { short: string; tone: PillTone } | null {
    const key = providerKey(sale);
    if (key === null) return null;
    return { short: PROVIDER_SHORT[key] ?? key, tone: PROVIDER_TONE[key] ?? 'neutral' };
}

/** Tono de cada estado de la venta; uno desconocido va neutro con su valor crudo. */
export const SALE_STATUS_TONE: Record<string, PillTone> = {
    COMPLETED: 'success',
    CANCELLED: 'danger',
    PENDING: 'warning',
};

/** Rótulo por defecto de cada estado, si el locale no lo trae. */
export const SALE_STATUS_DEFAULT_LABEL: Record<string, string> = {
    COMPLETED: 'Completada',
    CANCELLED: 'Cancelada',
    PENDING: 'Pendiente',
};

/** Cuántos artículos salieron en la venta: la suma de cantidades; la venta sin líneas es un equipo. */
export function saleItemCount(sale: Pick<BackofficeSale, 'lines'>): number {
    if (!sale.lines || sale.lines.length === 0) return 1;
    return sale.lines.reduce((sum, line) => sum + line.qty, 0);
}

/** La forma de pago: «Contado», o «Crédito» con su financiera aparte. */
export function salePayment(
    sale: FinancierableSale,
    labels: FinancieraLabels & { credito: string },
): { kind: string; provider: string | null } {
    if (sale.type === 'CASH' && !sale.externalProvider)
        return { kind: labels.contado, provider: null };
    return { kind: labels.credito, provider: financieraLabel(sale, labels) };
}
